import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export type ReportInPlaceExpandContextValue = {
  expandedSectionId: string | null
  expand: (id: string) => void
  exit: () => void
  isExpanded: (id: string) => boolean
}

const ReportInPlaceExpandContext =
  createContext<ReportInPlaceExpandContextValue | null>(null)

export function ReportInPlaceExpandProvider(props: {
  children: React.ReactNode
}): React.ReactElement {
  const { children } = props
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(
    null,
  )
  const expand = useCallback((id: string) => {
    setExpandedSectionId(id)
  }, [])
  const exit = useCallback(() => {
    setExpandedSectionId(null)
  }, [])
  const isExpanded = useCallback(
    (id: string) => expandedSectionId === id,
    [expandedSectionId],
  )
  const value = useMemo(
    () => ({ expandedSectionId, expand, exit, isExpanded }),
    [expandedSectionId, expand, exit, isExpanded],
  )
  return (
    <ReportInPlaceExpandContext.Provider value={value}>
      {children}
    </ReportInPlaceExpandContext.Provider>
  )
}

export function useReportInPlaceExpand(): ReportInPlaceExpandContextValue {
  const v = useContext(ReportInPlaceExpandContext)
  if (v == null) {
    throw new Error(
      'useReportInPlaceExpand must be used within ReportInPlaceExpandProvider',
    )
  }
  return v
}

export function useReportInPlaceExpandOptional(): ReportInPlaceExpandContextValue | null {
  return useContext(ReportInPlaceExpandContext)
}
